import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { findRegion } from '../utils/consentRegion';

const STORAGE_KEY = 'app.consent.analytics';
const ConsentContext = createContext({
  analyticsConsent: 'unknown',
  grantAnalytics: () => {},
  denyAnalytics: () => {},
  resetAnalytics: () => {},
  preferencesOpen: false,
  setPreferencesOpen: () => {},
  openPreferences: () => {},
  closePreferences: () => {},
});

export const readStoredAnalyticsConsent = (storage) => {
  if (!storage) return 'unknown';
  const value = storage.getItem(STORAGE_KEY);
  if (value === 'granted' || value === 'denied') return value;
  return 'unknown';
};

export const writeStoredAnalyticsConsent = (storage, value) => {
  if (!storage) return;
  if (value === 'granted' || value === 'denied') {
    storage.setItem(STORAGE_KEY, value);
  } else {
    storage.removeItem(STORAGE_KEY);
  }
};

// The visitor's country, from the pricing call the site already makes (Cloudflare's)
const lookupCountry = () => import('../api/client').then(({ api }) => api.getLocalPricing()).then((data) => data?.country);

/**
 * `analyticsConsent` is what the rest of the app acts on: an answer the visitor
 * gave always wins; with none, the EU/EEA, the UK and Switzerland are asked
 * ('unknown' shows the bar) and everywhere else starts 'granted' without being
 * asked (utils/consentRegion.js, the user's call on 2026-09-30). While the region
 * is not known yet it is 'pending': no bar and nothing recorded. Only a real
 * answer is written to storage, so "Reset decision" goes back to the default.
 */
export function ConsentProvider({ children, lookup = lookupCountry }) {
  const [choice, setAnalyticsConsent] = useState('unknown');
  const [region, setRegion] = useState('pending');
  const [loaded, setLoaded] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const stored = readStoredAnalyticsConsent(window.localStorage);
    if (stored !== 'unknown') {
      setAnalyticsConsent(stored);
    }
    setLoaded(true);
    let live = true;
    findRegion(lookup).then((found) => { if (live) setRegion(found); });
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleOpen = () => setPreferencesOpen(true);
    window.addEventListener('consent:open', handleOpen);
    return () => window.removeEventListener('consent:open', handleOpen);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !loaded) return;
    writeStoredAnalyticsConsent(window.localStorage, choice);
  }, [choice, loaded]);

  const analyticsConsent = choice !== 'unknown' ? choice
    : region === 'implied' ? 'granted'
    : region === 'ask' ? 'unknown'
    : 'pending';
  // For analytics to tell the two apart: said yes to the bar, or on by default
  const consentSource = choice === 'granted' ? 'accepted' : analyticsConsent === 'granted' ? 'default' : null;

  const grantAnalytics = () => setAnalyticsConsent('granted');
  const denyAnalytics = () => setAnalyticsConsent('denied');
  const resetAnalytics = () => setAnalyticsConsent('unknown');
  const openPreferences = () => setPreferencesOpen(true);
  const closePreferences = () => setPreferencesOpen(false);

  const value = useMemo(() => ({
    analyticsConsent,
    // Asked first (EU/EEA, UK, Switzerland) or on by default elsewhere
    consentRegion: region,
    consentSource,
    grantAnalytics,
    denyAnalytics,
    resetAnalytics,
    preferencesOpen,
    setPreferencesOpen,
    openPreferences,
    closePreferences,
  }), [analyticsConsent, region, consentSource, preferencesOpen]);

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

export const useConsent = () => useContext(ConsentContext);


